/**
 * マルチ端末同期用の SSE クライアント hook.
 *
 * サインイン中のみ `/sync/events` を購読する。EventSource は Authorization ヘッダを
 * 送れないので、`POST /sync/ticket` で取得した使い捨てチケット（60 秒・1 回限り）を
 * クエリパラメータで渡す。アクセストークンそのものは URL に載せない。
 *
 * 特徴:
 * - 自動再接続 (指数バックオフ)
 * - タブ復帰 (`visibilitychange` visible) 時の即時再接続
 * - `dispatchSyncEvent` 経由で各 Zustand store に通知
 */

import { useEffect, useRef } from 'react'

import { issueSyncTicket } from '@/api/api'
import { dispatchSyncEvent, type SyncEvent, type SyncTopic } from '@/api/sync-events'
import { useIsSignedIn } from '@/store/useAuthStore'

const apiUrl = import.meta.env.VITE_API_URL || '/api'

const DEFAULT_TOPICS: SyncTopic[] = [
  'todo',
  'nutrition',
  'meal_plan',
  'workout',
  'memo',
  'flashcard',
  'library_favorite',
  'library_selection',
  'download_queue',
  'ocr',
  'activity',
]

const MIN_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

type UseSseSyncOptions = {
  topics?: SyncTopic[]
}

export const useSseSync = (options: UseSseSyncOptions = {}): void => {
  const isSignedIn = useIsSignedIn()
  const esRef = useRef<EventSource | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const backoffRef = useRef<number>(MIN_BACKOFF_MS)
  const cancelledRef = useRef<boolean>(false)

  const topicsKey = (options.topics ?? DEFAULT_TOPICS).join(',')

  useEffect(() => {
    cancelledRef.current = false

    if (!isSignedIn) return

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const closeCurrent = () => {
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
    }

    const scheduleReconnect = () => {
      if (cancelledRef.current) return
      clearReconnectTimer()
      const delay = backoffRef.current
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS)
      reconnectTimerRef.current = setTimeout(() => {
        void connect()
      }, delay)
    }

    const connect = async () => {
      if (cancelledRef.current) return

      let ticket: string | null = null
      try {
        ticket = (await issueSyncTicket()).ticket
      } catch (err) {
        console.warn('[sync] issueSyncTicket failed:', err)
      }
      if (!ticket) {
        scheduleReconnect()
        return
      }
      if (cancelledRef.current) return

      closeCurrent()

      const url = `${apiUrl}/sync/events?ticket=${encodeURIComponent(ticket)}&topics=${encodeURIComponent(topicsKey)}`
      const es = new EventSource(url)
      esRef.current = es

      es.onopen = () => {
        backoffRef.current = MIN_BACKOFF_MS
      }

      // ブラウザは EventSource の "event: xxx" と "data: ..." を分けて配信する。
      // バックエンドが明示 event を付けているので addEventListener で受ける。
      const handleMessage = (evt: MessageEvent<string>) => {
        try {
          const parsed = JSON.parse(evt.data) as SyncEvent
          if (parsed.type === 'ping' || parsed.type === 'hello') return
          dispatchSyncEvent(parsed)
        } catch (err) {
          console.warn('[sync] parse failed:', err, evt.data)
        }
      }

      // 全 known イベントを購読
      const eventTypes = [
        'todo.created',
        'todo.updated',
        'todo.deleted',
        'nutrition.meal.created',
        'nutrition.meal.updated',
        'nutrition.meal.deleted',
        'nutrition.profile.updated',
        'nutrition.template.created',
        'nutrition.template.updated',
        'nutrition.template.deleted',
        'meal_plan.updated',
        'workout.session.changed',
        'workout.template.changed',
        'workout.exercise.changed',
        'workout.profile.changed',
        'memo.created',
        'memo.updated',
        'memo.deleted',
        'flashcard.created',
        'flashcard.updated',
        'flashcard.deleted',
        'library_favorite.created',
        'library_favorite.updated',
        'library_favorite.deleted',
        'library_selection.updated',
        'download_queue.updated',
        'ocr.updated',
        'notebooklm.updated',
        'activity.changed',
      ]
      for (const type of eventTypes) {
        es.addEventListener(type, handleMessage as EventListener)
      }
      // デフォルト message ハンドラも念のため
      es.onmessage = handleMessage

      es.onerror = () => {
        // EventSource の onerror はネットワーク切断・チケット失効等でも発火する。
        // EventSource の自動再接続は使用済みチケットで失敗するので、閉じて新しいチケットで繋ぎ直す。
        closeCurrent()
        scheduleReconnect()
      }
    }

    void connect()

    // タブ可視化復帰時は即時再接続
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && esRef.current === null) {
        backoffRef.current = MIN_BACKOFF_MS
        void connect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      cancelledRef.current = true
      clearReconnectTimer()
      closeCurrent()
      document.removeEventListener('visibilitychange', handleVisibility)
      backoffRef.current = MIN_BACKOFF_MS
    }
  }, [isSignedIn, topicsKey])
}
